import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  TablePagination,
  Chip,
  useTheme,
  useMediaQuery,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Apartment as CompanyIcon,
  TrendingUp as SkillingIcon
} from '@mui/icons-material';
import LeftNavigationBar from '../../../navbars/LeftNavigationBar';
import { getAllDPBeneficiaries, deleteDPBeneficiary } from '../../../redux/slices/mca/dPBeneficiaries/dPBeneficiaries';

const DPBeneficiariesControlPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { dpBeneficiaries, loading, error } = useSelector((state) => state.dpBeneficiaries);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    dispatch(getAllDPBeneficiaries());
  }, [dispatch]);

  const handleEdit = (id) => {
    navigate(`/degreeprogram/dp-beneficiaries-edit/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedBeneficiaryId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const resultAction = await dispatch(deleteDPBeneficiary(selectedBeneficiaryId));
      if (deleteDPBeneficiary.fulfilled.match(resultAction)) {
        setSnackbar({
          open: true,
          message: 'Beneficiary deleted successfully',
          severity: 'success',
        });
        dispatch(getAllDPBeneficiaries());
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to delete beneficiary',
        severity: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedBeneficiaryId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedBeneficiaryId(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAddBeneficiary = () => {
    navigate('/degreeprogram/dp-beneficiaries-add');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTypeFilterChange = (event) => {
    setTypeFilter(event.target.value);
    setPage(0); // Reset to first page when filter changes
  };

  const filteredBeneficiaries = dpBeneficiaries.filter((item) => {
    // Type filter
    if (typeFilter !== 'all' && item.type !== typeFilter) {
      return false;
    }
    
    // Search term filter
    return item && (
      (item.batch && item.batch.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.college && item.college.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.programme && item.programme.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.hours && item.hours.toString().includes(searchTerm)) ||
      (item.duration && item.duration.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.noOfCandidates && item.noOfCandidates.toString().includes(searchTerm)) ||
      (item.service?.title && item.service.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.business_service?.name && item.business_service.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.degree_program?.program_name && item.degree_program.program_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.company?.companyName && item.company.companyName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  if (loading) {
    return (
      <LeftNavigationBar
        Content={
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
            <CircularProgress size={60} />
          </Box>
        }
      />
    );
  }

  // Determine which columns to show based on viewport and selected type
  const getVisibleColumns = () => {
    if (isMobile) {
      return ['batch', 'noOfCandidates', 'actions'];
    } else if (isTablet) {
      return ['batch', 'noOfCandidates', 'business_service', 'service', 'company', 'actions'];
    } else {
      return ['batch', 'noOfCandidates', 'business_service', 'service', 'degree_program', 'company', 'actions', 
        ...(typeFilter === 'all' || typeFilter === 'degreeprogram' ? ['college', 'programme'] : []),
        ...(typeFilter === 'all' || typeFilter === 'skilling' ? ['hours', 'duration'] : [])
      ];
    }
  };

  const visibleColumns = getVisibleColumns();

  // Helper to check if column should be displayed
  const showColumn = (columnName) => visibleColumns.includes(columnName);

  return (
    <LeftNavigationBar
      Content={
        <Box sx={{ p: isMobile ? 2 : 3, maxWidth: 1600, mx: 'auto' }}>
          <Typography
            variant="h4"
            sx={{
              position: "relative",
              padding: 0,
              margin: 0,
              fontWeight: 300,
              fontSize: { xs: "32px", sm: "40px" },
              color: "#747474",
              textAlign: 'center',
              textTransform: "uppercase",
              paddingBottom: "5px",
              mb: 3,
              mt: -4,
              "&::before": {
                content: '""',
                width: "28px",
                height: "5px",
                display: "block",
                position: "absolute",
                bottom: "3px",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "#747474",
              },
              "&::after": {
                content: '""',
                width: "100px",
                height: "1px",
                display: "block",
                position: "relative",
                marginTop: "5px",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "#747474",
              },
            }}
          >
            Beneficiaries Panel
          </Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search beneficiaries..."
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
                sx={{
                  backgroundColor: 'background.paper',
                  borderRadius: 1,
                }}
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="type-filter-label">Type</InputLabel>
                <Select
                  labelId="type-filter-label"
                  id="type-filter"
                  value={typeFilter}
                  label="Type"
                  onChange={handleTypeFilterChange}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="degreeprogram">Degree Program</MenuItem>
                  <MenuItem value="skilling">Skilling</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={5} sx={{ textAlign: { xs: 'right', md: 'right' } }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddBeneficiary}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark
                  },
                  whiteSpace: 'nowrap',
                  width: { xs: '100%', md: 'auto' }
                }}
              >
                Add New Beneficiary
              </Button>
            </Grid>
          </Grid>

          <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.primary.main }}>
                    {showColumn('batch') && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>Batch</TableCell>}
                    
                    {showColumn('college') && typeFilter !== 'skilling' && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>College</TableCell>}
                    
                    {showColumn('noOfCandidates') && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>No. Candidates</TableCell>}
                    
                    {showColumn('programme') && typeFilter !== 'skilling' && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>Programme</TableCell>}
                    
                    {showColumn('hours') && typeFilter !== 'degreeprogram' && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>Hours</TableCell>}
                    
                    {showColumn('duration') && typeFilter !== 'degreeprogram' && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>Duration</TableCell>}
                    
                    {showColumn('business_service') && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>Business Service</TableCell>}
                    
                    {showColumn('service') && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>Service</TableCell>}
                    
                    {showColumn('degree_program') && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>Degree Program</TableCell>}
                    
                    {showColumn('company') && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }}>Company</TableCell>}
                    
                    {showColumn('actions') && 
                      <TableCell sx={{ color: 'white', fontWeight: 600, textAlign: "center" }} align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBeneficiaries.length > 0 ? (
                    filteredBeneficiaries
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((item) => (
                        <TableRow
                          key={item._id}
                          hover
                          sx={{
                            '&:nth-of-type(odd)': {
                              backgroundColor: theme.palette.action.hover,
                            },
                            '&:last-child td, &:last-child th': { border: 0 }
                          }}
                        >
                          {showColumn('batch') && 
                            <TableCell sx={{ textAlign: "center" }}>{item.batch || 'N/A'}</TableCell>}
                          
                          {showColumn('college') && typeFilter !== 'skilling' && 
                            <TableCell sx={{ textAlign: "center" }}>
                              {item.type === 'degreeprogram' ? item.college || 'N/A' : 'N/A'}
                            </TableCell>}
                          
                          {showColumn('noOfCandidates') && 
                            <TableCell sx={{ textAlign: "center" }}>
                              <Chip 
                                label={item.noOfCandidates || '0'} 
                                size="small" 
                                color="info" 
                                sx={{ fontWeight: 'bold' }} 
                              />
                            </TableCell>}
                          
                          {showColumn('programme') && typeFilter !== 'skilling' && 
                            <TableCell sx={{ textAlign: "center" }}>
                              {item.type === 'degreeprogram' ? item.programme || 'N/A' : 'N/A'}
                            </TableCell>}
                          
                          {showColumn('hours') && typeFilter !== 'degreeprogram' && 
                            <TableCell sx={{ textAlign: "center" }}>
                              {item.type === 'skilling' ? item.hours || 'N/A' : 'N/A'}
                            </TableCell>}
                          
                          {showColumn('duration') && typeFilter !== 'degreeprogram' && 
                            <TableCell sx={{ textAlign: "center" }}>
                              {item.type === 'skilling' ? item.duration || 'N/A' : 'N/A'}
                            </TableCell>}
                          
                          {showColumn('business_service') && 
                            <TableCell sx={{ textAlign: "center" }}>
                              <Chip 
                                label={item.business_service?.name || 'N/A'} 
                                size="small" 
                                color="secondary" 
                              />
                            </TableCell>}
                          
                          {showColumn('service') && 
                            <TableCell sx={{ textAlign: "center" }}>
                              <Chip
                                label={item.service?.title || 'N/A'}
                                size="small"
                                color="primary"
                                icon={<BusinessIcon fontSize="small" />}
                              />
                            </TableCell>}
                          
                          {showColumn('degree_program') && 
                            <TableCell sx={{ textAlign: "center" }}>
                              <Chip
                                label={item.degree_program?.program_name || 'N/A'}
                                size="small"
                                icon={item.type === 'degreeprogram' ? <SchoolIcon fontSize="small" /> : <SkillingIcon fontSize="small" />}
                                color={item.type === 'degreeprogram' ? 'default' : 'warning'}
                              />
                            </TableCell>}
                          
                          {showColumn('company') && 
                            <TableCell sx={{ textAlign: "center" }}>
                              <Chip 
                                label={item.company?.companyName || 'N/A'} 
                                size="small" 
                                icon={<CompanyIcon fontSize="small" />} 
                              />
                            </TableCell>}
                          
                          {showColumn('actions') && 
                            <TableCell align="center" sx={{ textAlign: "center" }}>
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                <Tooltip title="Edit">
                                  <Button
                                    onClick={() => handleEdit(item._id)}
                                    color="primary"
                                    size={isMobile ? 'small' : 'medium'}
                                    variant="outlined"
                                  >
                                    <EditIcon />
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <Button
                                    onClick={() => handleDeleteClick(item._id)}
                                    color="error"
                                    size={isMobile ? 'small' : 'medium'}
                                    variant="outlined"
                                  >
                                    <DeleteIcon />
                                  </Button>
                                </Tooltip>
                              </Box>
                            </TableCell>}
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          {typeFilter === 'degreeprogram' ? (
                            <SchoolIcon color="disabled" sx={{ fontSize: 60, mb: 1 }} />
                          ) : typeFilter === 'skilling' ? (
                            <SkillingIcon color="disabled" sx={{ fontSize: 60, mb: 1 }} />
                          ) : (
                            <BusinessIcon color="disabled" sx={{ fontSize: 60, mb: 1 }} />
                          )}
                          <Typography variant="h6" color="text.secondary">
                            No beneficiaries found
                          </Typography>
                          {searchTerm && (
                            <Typography variant="body2" color="text.secondary">
                              No results for "{searchTerm}"
                            </Typography>
                          )}
                          {typeFilter !== 'all' && (
                            <Typography variant="body2" color="text.secondary">
                              Try changing the type filter
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {filteredBeneficiaries.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredBeneficiaries.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                  '& .MuiTablePagination-toolbar': {
                    paddingLeft: 2,
                    paddingRight: 1
                  }
                }}
              />
            )}
          </Paper>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            PaperProps={{
              sx: {
                borderRadius: 2,
                minWidth: isMobile ? '90%' : 400,
              }
            }}
          >
            <DialogTitle sx={{
              backgroundColor: theme.palette.error.light,
              color: 'white',
              fontWeight: 600
            }}>
              Confirm Deletion
            </DialogTitle>
            <DialogContent sx={{ py: 3 }}>
              <Typography variant="body1">
                Are you sure you want to delete this beneficiary? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={handleDeleteCancel}
                variant="outlined"
                sx={{
                  borderColor: theme.palette.grey[400],
                  color: theme.palette.text.primary
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                variant="contained"
                color="error"
                sx={{
                  backgroundColor: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.error.dark
                  }
                }}
              >
                Delete
              </Button>
            </DialogActions>
          </Dialog>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              variant="filled"
              sx={{ width: '100%' }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      }
    />
  );
};

export default DPBeneficiariesControlPage;